import { useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, CheckCircle, Phone } from '@phosphor-icons/react';
import { NAP } from '@/data/site';
import { PROJECT_LANDINGS, buildProjectLead } from '@/data/projectLandings';
import { submitLead, formatApiErrorDetail } from '@/lib/api';
import { useSEO, serviceLd, breadcrumbLd, faqLd } from '@/lib/seo';

const EMPTY_FORM = { name: '', email: '', phone: '', location: '', selection: '', timeline: '', message: '', company_website: '' };
const inputClass = 'w-full bg-background border border-border px-4 py-3 text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';

function ProjectEnquiry({ slug, page }) {
  const { search } = useLocation();
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const change = event => setForm(current => ({ ...current, [event.target.name]: event.target.value }));
  const send = async event => {
    event.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    setError('');
    try {
      await submitLead(buildProjectLead(slug, form, search));
      setStatus('sent');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail || err.message));
      setStatus('error');
    }
  };

  if (status === 'sent') return (
    <div role="status" className="border border-primary bg-primary/5 p-8" data-testid="project-enquiry-success">
      <CheckCircle size={40} className="text-primary" />
      <h3 className="font-display text-3xl uppercase mt-4">Your project is with us.</h3>
      <p className="text-muted-foreground mt-4">Thanks, {form.name.split(' ')[0]}. MRE will review your details and contact you to discuss the next steps.</p>
      <Link to={`/services/${page.service}`} className="inline-flex items-center gap-2 text-primary mt-6">Explore {page.name.toLowerCase()} <ArrowRight /></Link>
    </div>
  );

  return (
    <form onSubmit={send} className="space-y-5" data-testid="project-enquiry" aria-busy={status === 'sending'}>
      <fieldset disabled={status === 'sending'} className="space-y-5">
        <legend className="sr-only">Your {page.name.toLowerCase()} project</legend>
        <label className="block text-sm font-medium" htmlFor="project-selection">{page.selectionLabel} *</label>
        <select id="project-selection" name="selection" required value={form.selection} onChange={change} className={inputClass}>
          <option value="">Choose an option</option>
          {page.options.map(option => <option key={option}>{option}</option>)}
        </select>
        <div className="grid sm:grid-cols-2 gap-5">
          {[
            ['name', 'Your name', 'text', 'name', true],
            ['email', 'Email', 'email', 'email', true],
            ['phone', 'Phone', 'tel', 'tel', false],
            ['location', 'Community / postal code', 'text', 'postal-code', true],
          ].map(([name, label, type, autoComplete, required]) => (
            <div key={name}>
              <label htmlFor={`project-${name}`} className="block text-sm mb-2">{label}{required ? ' *' : ' (optional)'}</label>
              <input id={`project-${name}`} name={name} type={type} required={required} autoComplete={autoComplete} maxLength={200} value={form[name]} onChange={change} className={inputClass} />
            </div>
          ))}
        </div>
        <div>
          <label htmlFor="project-timeline" className="block text-sm mb-2">When would you like to start? *</label>
          <select id="project-timeline" name="timeline" required value={form.timeline} onChange={change} className={inputClass}>
            <option value="">Choose your timing</option>
            {['As soon as practical', 'Within 3 months', '3–6 months', '6–12 months', 'Exploring options'].map(option => <option key={option}>{option}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="project-message" className="block text-sm mb-2">What would you like us to know? (optional)</label>
          <textarea id="project-message" name="message" rows={4} maxLength={4000} value={form.message} onChange={change} placeholder="Your goals, property details, or questions for the team…" className={inputClass} />
        </div>
        <div className="hp-trap" aria-hidden="true">
          <label>Company website<input name="company_website" tabIndex={-1} autoComplete="off" value={form.company_website} onChange={change} /></label>
        </div>
      </fieldset>
      <p className="text-xs text-muted-foreground leading-relaxed">We’ll use these details to respond to your project enquiry.</p>
      {error && <p role="alert" className="text-destructive text-sm">{error} You can also call <a className="underline" href={`tel:${NAP.phoneHref}`}>{NAP.phoneDisplay}</a>.</p>}
      <button disabled={status === 'sending'} className="w-full flex items-center justify-center gap-3 bg-primary text-primary-foreground py-4 font-mono-tech text-xs uppercase tracking-widest disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">
        {status === 'sending' ? 'Sending your enquiry…' : 'Request my consultation'} <ArrowRight size={18} />
      </button>
      <p className="text-xs text-muted-foreground">Free consultation. Your scope reviewed before a quote.</p>
    </form>
  );
}

export default function ProjectLanding() {
  const { slug } = useParams();
  const page = PROJECT_LANDINGS[slug];
  useSEO({
    title: page ? `${page.name} in Calgary | MRE Developments` : 'MRE Developments',
    description: page?.intro || '', path: `/start/${slug}`, image: page ? `https://mredevelopments.com${page.image}` : undefined,
    jsonLd: page ? [serviceLd(page.name, page.intro), faqLd(page.questions), breadcrumbLd([{ name: 'Home', path: '/' }, { name: page.name, path: `/start/${slug}` }])] : [],
  });
  if (!page) return <Navigate to="/services" replace />;
  return (
    <div data-testid={`landing-${slug}`}>
      <section className="border-b border-border pt-20">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2">
          <div className="px-5 sm:px-8 pt-16 pb-14 lg:py-24 lg:pr-14 flex flex-col justify-center">
            <p className="font-mono-tech text-xs tracking-widest uppercase text-primary">MRE Developments · Calgary & area</p>
            <h1 className="font-display text-5xl sm:text-6xl xl:text-7xl uppercase leading-[0.98] tracking-tight mt-7">{page.headline}</h1>
            <p className="text-lg text-muted-foreground leading-relaxed mt-7 max-w-xl">{page.intro}</p>
            <div className="flex flex-wrap items-center gap-6 mt-9">
              <a href="#project-enquiry" className="inline-flex items-center gap-3 bg-primary text-primary-foreground px-6 py-4 font-mono-tech text-xs uppercase tracking-widest">Plan my project <ArrowRight size={18} /></a>
              <a href={`tel:${NAP.phoneHref}`} className="inline-flex gap-2 items-center text-sm hover:text-primary"><Phone />{NAP.phoneDisplay}</a>
            </div>
            <p className="font-mono-tech uppercase text-[10px] tracking-widest text-muted-foreground mt-8">{page.name} · Free consultation</p>
          </div>
          <figure className="relative min-h-[320px] lg:min-h-[620px] m-0">
            <img src={page.image} alt={page.imageAlt} className="absolute w-full h-full object-cover" fetchPriority="high" />
            <figcaption className="absolute bottom-5 left-5 bg-background/90 border border-border px-4 py-2 font-mono-tech text-[10px] uppercase tracking-widest">MRE project photography</figcaption>
          </figure>
        </div>
      </section>
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 lg:py-24">
        <div className="flex flex-wrap items-end justify-between gap-5 mb-8">
          <div><p className="text-primary font-mono-tech text-xs uppercase tracking-widest">A useful starting point</p><h2 className="font-display text-4xl uppercase mt-3">{page.sectionTitle}</h2></div>
          <Link to={`/services/${page.service}`} className="inline-flex items-center gap-2 text-sm hover:text-primary">Explore the service <ArrowUpRight /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {page.options.map((option, index) => <a key={option} href="#project-enquiry" className="border border-border p-6 hover:border-primary transition-colors"><span className="text-primary font-mono-tech text-xs">0{index + 1}</span><h3 className="font-display text-2xl uppercase mt-4">{option}</h3></a>)}
        </div>
        <p className="text-sm text-muted-foreground mt-6 max-w-3xl">{page.note}</p>
      </section>
      <section className="border-y border-border bg-secondary/20" id="project-enquiry" style={{ scrollMarginTop: '6rem' }}>
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <p className="font-mono-tech text-xs uppercase tracking-widest text-primary">Let’s talk about your project</p>
            <h2 className="font-display text-4xl sm:text-5xl uppercase mt-4">Make the first conversation count.</h2>
            <p className="text-muted-foreground leading-relaxed mt-6">Share the basics now so we can start with your plans, your timing and the right next step.</p>
            <ol className="mt-10 space-y-7">
              {['Tell us about the property and the work.', 'We review your scope and contact you.', 'Together, we establish the next steps for a quote.'].map((text, index) => <li key={text} className="flex gap-4"><span className="text-primary font-mono-tech">0{index + 1}</span><span>{text}</span></li>)}
            </ol>
          </div>
          <div className="border border-border bg-card p-5 sm:p-8"><ProjectEnquiry key={slug} slug={slug} page={page} /></div>
        </div>
      </section>
      <section className="max-w-[1000px] mx-auto px-5 sm:px-8 py-16 lg:py-24">
        <h2 className="font-display text-4xl uppercase mb-8">Before we speak.</h2>
        {page.questions.map(question => <details key={question.q} className="border-t border-border py-5"><summary className="cursor-pointer text-lg font-medium">{question.q}</summary><p className="mt-4 text-muted-foreground leading-relaxed">{question.a}</p></details>)}
      </section>
    </div>
  );
}
